import { Button, Field, Text, Textarea } from '@components/ui';
import {
  type SupportFormValues,
  supportSchema,
} from '@features/support/supportSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiErrorMessage } from '@lib/api/apiError';
import { useSupportMessageMutation } from '@lib/api/support';
import Box from '@mui/material/Box';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';


const Support = (): JSX.Element => {
  const { t } = useTranslation();
  const sendSupportMessage = useSupportMessageMutation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<SupportFormValues>({
    defaultValues: {
      message: '',
    },
    resolver: zodResolver(supportSchema),
    mode: 'onChange',
  });
  const messageField = register('message');

  const onSubmit = async ({ message }: SupportFormValues): Promise<void> => {
    try {
      await sendSupportMessage.mutateAsync(message);
      toast.success('Your support request has been sent');
      reset();
    } catch (error: unknown) {
      toast.error(
        apiErrorMessage(error) || 'Unable to send support request'
      );
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1120,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 3,
        boxSizing: 'border-box',
      }}
    >
      <Text variant="body" tone="muted" sx={{ display: 'block', mb: 2 }}>
        {t('supportForm.description')}
      </Text>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Field
          error={errors.message ? t(errors.message.message ?? '') : undefined}
        >
          <Textarea
            placeholder={t('supportForm.placeholder')}
            invalid={Boolean(errors.message)}
            minRows={8}
            name={messageField.name}
            onChange={messageField.onChange}
            onBlur={messageField.onBlur}
            inputRef={messageField.ref}
          />
        </Field>
        <Button
          type="submit"
          variant="secondary"
          size="lg"
          fullWidth
          disabled={!isValid}
          loading={sendSupportMessage.isPending}
        >
          {t('common.submit')}
        </Button>
      </Box>
    </Box>
  );
};

export default Support;
